import { useEffect, useRef } from "react";

const DEFAULT_IGNORE_SELECTOR = [
  "input",
  "textarea",
  "select",
  "button",
  "a",
  "[contenteditable='true']",
  "[role='dialog']",
  "[data-swipe-back-disabled='true']",
  ".no-swipe-back",
  ".no-ios-swipe-back",
  ".admin-native-schedule-scroll",
  ".admin-native-schedule-inner",
  ".admin-native-grid",
  ".loop-resource-day-scroll",
  ".loop-resource-day-shell",
  ".fc-scroller",
  ".fc",
  ".nb-preview-modal",
  ".nb-preview-sheet",
  ".nb-live-preview",
  ".nb-live-preview-phone",
  ".swiper",
  ".carousel",
  ".embla",
].join(",");

function isNativeIosOrIosWeb() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor || null;
    const platform = cap
      ? (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform)
      : null;
    if (platform === "ios") return true;

    const ua = window.navigator?.userAgent || "";
    const touchMac = window.navigator?.platform === "MacIntel" && window.navigator?.maxTouchPoints > 1;
    return /iPad|iPhone|iPod/i.test(ua) || touchMac;
  } catch (_) {
    return false;
  }
}

function hasRouteHistory() {
  try {
    const idx = window.history?.state?.idx;
    if (typeof idx === "number") return idx > 0;
    return window.history.length > 1;
  } catch (_) {
    return false;
  }
}

function isVisibleModalOpen() {
  try {
    const selectors = [
      "[role='dialog']",
      "[aria-modal='true']",
      ".modal",
      ".svc-modal",
      ".nb-preview-modal",
      ".booking-create-modal",
      ".event-view-modal",
      ".admin-smart-create",
      ".admin-manual-edit",
  ".loop-calendar-modal-root",
  ".loop-services-editor-backdrop",
  ".booking-pro-modal",
  ".loop-create-pro-modal",
  ".avatar-menu-panel",
  ".gen-modal",
  ".booking-service-portal-menu",
    ];
    return selectors.some((selector) => {
      const el = document.querySelector(selector);
      if (!el) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden" && Number(style.opacity || 1) !== 0;
    });
  } catch (_) {
    return false;
  }
}

function targetShouldIgnore(target, ignoreSelector = DEFAULT_IGNORE_SELECTOR) {
  try {
    if (!target || typeof target.closest !== "function") return false;
    if (target.closest(ignoreSelector)) return true;

    let el = target;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowX = `${style.overflowX || ""} ${style.overflow || ""}`;
      const canScrollX = /(auto|scroll)/.test(overflowX) && el.scrollWidth > el.clientWidth + 8;
      if (canScrollX) return true;
      el = el.parentElement;
    }
    return false;
  } catch (_) {
    return false;
  }
}

function ensureIndicator() {
  if (typeof document === "undefined") return null;

  let style = document.getElementById("loop-swipe-back-style");
  if (!style) {
    style = document.createElement("style");
    style.id = "loop-swipe-back-style";
    style.textContent = `
      .loop-swipe-back-indicator {
        position: fixed;
        left: 10px;
        top: 50%;
        width: 46px;
        height: 46px;
        transform: translate3d(-18px, -50%, 0) scale(.86);
        opacity: 0;
        pointer-events: none;
        z-index: 2147483646;
        border-radius: 999px;
        background: rgba(255,255,255,.94);
        border: 1px solid rgba(226,232,240,.92);
        box-shadow: 0 16px 42px rgba(15,23,42,.18);
        display: grid;
        place-items: center;
        color: #00675f;
        transition: opacity 120ms ease, transform 120ms ease;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
      }
      .loop-swipe-back-indicator::before {
        content: "";
        width: 13px;
        height: 13px;
        border-left: 3px solid currentColor;
        border-bottom: 3px solid currentColor;
        transform: rotate(45deg) translate(2px, -1px);
        border-radius: 2px;
      }
      .loop-swipe-back-indicator.is-visible {
        opacity: var(--loop-swipe-opacity, .7);
        transform: translate3d(var(--loop-swipe-x, 0px), -50%, 0) scale(var(--loop-swipe-scale, 1));
      }
      body.loop-swipe-back-active {
        overscroll-behavior-x: contain;
      }
    `;
    document.head.appendChild(style);
  }

  let indicator = document.getElementById("loop-swipe-back-indicator");
  if (!indicator) {
    indicator = document.createElement("div");
    indicator.id = "loop-swipe-back-indicator";
    indicator.className = "loop-swipe-back-indicator";
    indicator.setAttribute("aria-hidden", "true");
    document.body.appendChild(indicator);
  }
  return indicator;
}

export default function useSwipeBackGesture({
  enabled = true,
  edgeWidth = 28,
  threshold = 86,
  maxVerticalTravel = 72,
  ignoreSelector = DEFAULT_IGNORE_SELECTOR,
  onBack,
} = {}) {
  const stateRef = useRef({
    active: false,
    committed: false,
    tracking: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!isNativeIosOrIosWeb()) return undefined;

    const indicator = ensureIndicator();

    const resetIndicator = () => {
      try {
        indicator?.classList.remove("is-visible");
        indicator?.style.removeProperty("--loop-swipe-x");
        indicator?.style.removeProperty("--loop-swipe-opacity");
        indicator?.style.removeProperty("--loop-swipe-scale");
        document.body.classList.remove("loop-swipe-back-active");
      } catch (_) {}
    };

    const start = (event) => {
      try {
        const touch = event.touches?.[0];
        if (!touch) return;
        if (touch.clientX > edgeWidth) return;
        if (!hasRouteHistory()) return;
        if (isVisibleModalOpen()) return;
        if (targetShouldIgnore(event.target, ignoreSelector)) return;

        stateRef.current = {
          active: true,
          committed: false,
          tracking: false,
          startX: touch.clientX,
          startY: touch.clientY,
          lastX: touch.clientX,
          lastY: touch.clientY,
        };
      } catch (_) {}
    };

    const move = (event) => {
      const state = stateRef.current;
      if (!state.active) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      state.lastX = touch.clientX;
      state.lastY = touch.clientY;

      if (dx < 0) {
        stateRef.current.active = false;
        resetIndicator();
        return;
      }

      if (Math.abs(dy) > maxVerticalTravel && Math.abs(dy) > dx) {
        stateRef.current.active = false;
        resetIndicator();
        return;
      }

      if (dx < 8) return;
      state.tracking = true;

      try {
        event.preventDefault();
      } catch (_) {}

      const progress = Math.max(0, Math.min(1, dx / threshold));
      try {
        document.body.classList.add("loop-swipe-back-active");
        indicator?.classList.add("is-visible");
        indicator?.style.setProperty("--loop-swipe-x", `${Math.min(54, dx * 0.34)}px`);
        indicator?.style.setProperty("--loop-swipe-opacity", String(0.2 + progress * 0.8));
        indicator?.style.setProperty("--loop-swipe-scale", String(0.88 + progress * 0.12));
      } catch (_) {}
    };

    const end = () => {
      const state = stateRef.current;
      if (!state.active) {
        resetIndicator();
        return;
      }

      const dx = state.lastX - state.startX;
      const dy = Math.abs(state.lastY - state.startY);
      const shouldGoBack = state.tracking && dx >= threshold && dy <= maxVerticalTravel;

      stateRef.current = {
        active: false,
        committed: shouldGoBack,
        tracking: false,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
      };

      resetIndicator();

      if (shouldGoBack) {
        window.setTimeout(() => {
          try {
            if (typeof onBack === "function") onBack();
            else window.history.back();
          } catch (_) {
            try { window.history.back(); } catch (_) {}
          }
        }, 20);
      }
    };

    const cancel = () => {
      stateRef.current.active = false;
      resetIndicator();
    };

    document.addEventListener("touchstart", start, { passive: true, capture: true });
    document.addEventListener("touchmove", move, { passive: false, capture: true });
    document.addEventListener("touchend", end, { passive: true, capture: true });
    document.addEventListener("touchcancel", cancel, { passive: true, capture: true });

    return () => {
      document.removeEventListener("touchstart", start, { capture: true });
      document.removeEventListener("touchmove", move, { capture: true });
      document.removeEventListener("touchend", end, { capture: true });
      document.removeEventListener("touchcancel", cancel, { capture: true });
      resetIndicator();
    };
  }, [enabled, edgeWidth, threshold, maxVerticalTravel, ignoreSelector, onBack]);
}
